import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { DualListComponent } from "angular-dual-listbox";
import { HttpErrorsService } from "@sharedV11/services/alerts/errors/http-errors.service";
import { SnackbarInfoService } from "@sharedV11/services/alerts/snackbar-info/snackbar-info.service";
import { ConectarApiService } from "@sharedV11/services/api/connection/conectar-api.service";
import { GlobalService } from "@sharedV11/services/global/global.service";
import { GlobalServicCentros } from "@sharedV11/services/global/global.service centros";
import { PanelService } from "@sharedV11/services/panel/panel.service";
import { Translator } from "@sharedV11/services/translator/translator.service";
import {
  normalizeTaxonomiaNivelNombreForDisplay,
  txTaxonomyFieldLabel,
} from "src/app/shared/utils/column-label.util";

/** Fila de la sección Entradas (producto + control de acceso para InsUpdTipoAbonado). */
export interface TipoAbonadoEntradaFila {
  _uid: string;
  tipoEntradaId: string | null;
  controlAcceso: boolean;
}

@Component({
  selector: "app-edit-tipo-abonado",
  templateUrl: "./edit-tipo-abonado.component.html",
  styleUrls: ["./edit-tipo-abonado.component.scss"],
})
export class EditTipoAbonadoComponent implements OnInit {
  loading = false;
  @Input() pkId: string;
  @Input() mode: string;
  @Input() modificar: boolean;

  editRegistroForm: FormGroup;
  translation: any;

  // listas
  gruposTipoAbonado: any[] = [];
  tiposProductos: any[] = [];
  categorias: any[] = [];
  tiposControl: any[] = [];
  gruposEdad: any[] = [];
  gruposReport: any[] = [];
  tiposEntradaDatos: any[] = [];
  canales: any[] = [];
  productosDisponibles: any[] = [];
  botoneraOpciones: any[] = [];
  txCentroCoste: any[] = [];
  txTipoPrecio: any[] = [];
  txCanalVentas: any[] = [];
  txNiveles: { [nivel: number]: any[] } = {};
  taxonomiaRevenueCategory = "";
  taxonomiaCuentaVisitante = "";
  ultimoNivelTaxonomiaLeaf: any = null;

  /** Muestra el bloque de info (Revenue category / Cuenta visitante) solo cuando hay nivel final (TieneSucesor=0). */
  get mostrarTaxonomiaInfo(): boolean {
    return !!(this.taxonomiaRevenueCategory || this.mostrarCuentaVisitante);
  }

 
  get mostrarCuentaVisitante(): boolean {
    return !!this.taxonomiaCuentaVisitante;
  }

  /** Etiqueta de solo lectura: en alta vacío se muestra "—", no un input desactivado. */
  get textoEtiquetaCodigo(): string {
    const raw = this.editRegistroForm?.get("Codigo")?.value;
    if (raw === null || raw === undefined) {
      return "—";
    }
    const s = String(raw).trim();
    return s === "" ? "—" : s;
  }

  get labelTxCentroCoste(): string {
    const t = this.translator.GetTranslations() || this.translation || {};
    return txTaxonomyFieldLabel(t, "CentroCoste", "Centro coste");
  }

  get labelTxTipoPrecio(): string {
    const t = this.translator.GetTranslations() || this.translation || {};
    return txTaxonomyFieldLabel(t, "TipoPrecio", "Tipo precio");
  }

  get labelTxCanalVentas(): string {
    const t = this.translator.GetTranslations() || this.translation || {};
    return txTaxonomyFieldLabel(t, "CanalVentas", "Canal ventas");
  }

  displayTaxonomiaNivelNombre(item: any): string {
    return normalizeTaxonomiaNivelNombreForDisplay(item?.NivelNombre);
  }

  private textoCuentaVisitanteInformativo(cv: any): string {
    if (cv === null || cv === undefined) {
      return "";
    }
    if (typeof cv === "string" && cv.trim() === "") {
      return "";
    }
    const n =
      typeof cv === "number"
        ? cv
        : parseInt(String(cv).trim(), 10);
    if (Number.isNaN(n) || n <= -1) {
      return "";
    }
    if (n === 0) {
      return this.translation?.No ?? "No";
    }
    if (n === 1) {
      return this.translation?.Si ?? "Si";
    }
    return "";
  }

  // dual list
  format = {
    add: "Añadir",
    remove: "Borrar",
    all: "Todos",
    none: "Ninguno",
    direction: DualListComponent.LTR,
    draggable: true,
    locale: "es",
  };
  /** LEGACY dual-list producto (HTML comentado); mantener arrays vacíos. */
  productosSinAsignar: any[] = [];
  productosAsignados: any[] = [];

  entradasRows: TipoAbonadoEntradaFila[] = [];

  centrosSinAsignar: any[] = [];
  centrosAsignados: any[] = [];

  dualListHeight = "420px";

  /** Subtítulo sección Centros solo en Tipo abonado (no reutilizar `CentrosHint` de temporadas). */
  readonly centrosSectionHintFallback =
    "Asigne los centros que pertenecen a este tipo de abonado.";

  submitAttempted = false;
  submitValidationTick = 0;

  /** Evita bucles: al sincronizar cabecera TX desde la hoja no debe dispararse el reset de niveles. */
  private taxonomiaCabeceraWatchInited = false;

  asociarCentros = false;
  
  asociarCentrosCosteCanales = false;
  obligatorioCanalesEntradas = false;
  taxonomiaActiva = false;
  grupoReportAbonado = false;

  constructor(
    private fb: FormBuilder,
    private translator: Translator,
    public panelService: PanelService,
    private snackvar_service: SnackbarInfoService,
    private servicioAPI: ConectarApiService,
    private httpErrorService: HttpErrorsService,
    private global: GlobalService,
    private gsCentros: GlobalServicCentros,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.translation = this.translator.GetTranslations();
    this.initConfigFlags();
    this.initDualListFormat();
    this.buildForm();
    this.cargarDatosAsync();
  }

  private static flagActivo(valor: string | null | undefined): boolean {
    if (valor == null || valor === "") return false;
    const s = String(valor).trim().toLowerCase();
    return s === "1" || s === "true";
  }

  /** Flags AppConfigJsonCustommanConfigOtros: ya en localStorage tras login / redirect-ms (sin GetAppConfig aquí). */
  private initConfigFlags(): void {
    this.asociarCentros = EditTipoAbonadoComponent.flagActivo(
      localStorage.getItem("AsociarCentros")
    );
    this.asociarCentrosCosteCanales = EditTipoAbonadoComponent.flagActivo(
      localStorage.getItem("AsociarCentrosCosteCanales")
    );
    this.obligatorioCanalesEntradas = EditTipoAbonadoComponent.flagActivo(
      localStorage.getItem("ObligatorioCanalesEntradas")
    );
    this.taxonomiaActiva = EditTipoAbonadoComponent.flagActivo(
      localStorage.getItem("Taxonomia")
    );
    this.grupoReportAbonado = EditTipoAbonadoComponent.flagActivo(
      localStorage.getItem("GrupoReportAbonado")
    );
  }

  private initDualListFormat(): void {
    this.format = {
      add: this.translation?.Anadir || "Añadir",
      remove: this.translation?.Borrar || "Borrar",
      all: this.translation?.Todos || "Todos",
      none: this.translation?.Ninguno || "Ninguno",
      direction: DualListComponent.LTR,
      draggable: true,
      locale: "es",
    };
  }

  /** Secuencias prohibidas por la API (mismo criterio que Temporadas/Taxonomía). */
  private static readonly CARACTERES_PROHIBIDOS = ["--", ";", "/*", "*/", "@@"];

  static caracteresProhibidosValidator(control: AbstractControl): ValidationErrors | null {
    const v = (control.value ?? "").toString();
    if (!v) return null;
    const encontrado = EditTipoAbonadoComponent.CARACTERES_PROHIBIDOS.find((seq) =>
      v.includes(seq)
    );
    return encontrado ? { caracteresProhibidos: { secuencia: encontrado } } : null;
  }

  static sanitizarCaracteresProhibidos(valor: string): string {
    let s = (valor ?? "").toString();
    EditTipoAbonadoComponent.CARACTERES_PROHIBIDOS.forEach((seq) => {
      s = s.split(seq).join("");
    });
    return s;
  }

  /**
   * Respuesta ObtenerTiposAbonado: flags pueden venir "0"/"1", 0/1 o "True"/"False".
   */
  private static flagBooleanoDesdeApi(v: any): boolean {
    if (v === true || v === 1) return true;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      return s === "1" || s === "true";
    }
    return false;
  }

  /**
   * InsUpdTipoAbonado: ImpresionConjunta — string "1"/"0".
   */
  private static flagString01(v: any): "0" | "1" {
    if (v === true || v === 1) return "1";
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "1" || s === "true") return "1";
      if (s === "0" || s === "false") return "0";
    }
    return "0";
  }

  /** ALaVenta, ImprimirTicket, TicketVendido — "True"/"False". */
  private static flagTrueFalseApi(v: any): "True" | "False" {
    return EditTipoAbonadoComponent.flagString01(v) === "1" ? "True" : "False";
  }

  private static toggleOpcionVentaRequerida(
    control: AbstractControl
  ): ValidationErrors | null {
    const v = control.value;
    if (v === null || v === undefined) {
      return { required: true };
    }
    return null;
  }

  private static strId(v: any): string {
    if (v === null || v === undefined || v === "") return "";
    return String(v);
  }

  private static newEntradaFilaUid(): string {
    return (
      "e" +
      Date.now().toString(36) +
      Math.random().toString(36).slice(2, 10)
    );
  }

  /** ListaTiposEntradas.ControlAcceso desde API ("0"|"1", boolean, etc.). */
  private static controlAccesoDesdeListaApi(v: any): boolean {
    return EditTipoAbonadoComponent.flagBooleanoDesdeApi(v);
  }

  addEntradaRow(): void {
    this.entradasRows = [
      ...this.entradasRows,
      {
        _uid: EditTipoAbonadoComponent.newEntradaFilaUid(),
        tipoEntradaId: null,
        controlAcceso: false,
      },
    ];
    this.panelService.setDiscardChanges(false);
  }

  removeEntradaRow(index: number): void {
    this.entradasRows = this.entradasRows.filter((_, i) => i !== index);
    this.panelService.setDiscardChanges(false);
  }

  trackEntradaRow(_index: number, row: TipoAbonadoEntradaFila): string {
    return row._uid;
  }

  /**
   * Nodo hoja de GetTXItemVentasSegunNivel: el back puede devolver TXCanalVentasId / TXTipoPrecioId / etc.
   * además de TXItemVentasId; son la referencia coherente con el ítem de venta elegido.
   */
  private static idTaxonomiaDesdeNodoIfAny(
    nodo: any,
    claves: string[]
  ): string | null {
    if (!nodo || typeof nodo !== "object") {
      return null;
    }
    for (const k of claves) {
      const val = nodo[k];
      if (val === null || val === undefined) {
        continue;
      }
      const s = String(val).trim();
      if (s === "" || s === "0") {
        continue;
      }
      return s;
    }
    return null;
  }

  /**
   * mat-select de idTipoOperacion usa [value] numérico 1|2|3; la API devuelve "1"|"2"|"3".
   */
  private static idTipoOperacionParaSelect(v: any): number | null {
    if (v === null || v === undefined || v === "") return null;
    const n = parseInt(String(v).trim(), 10);
    if (n === 1 || n === 2 || n === 3) return n;
    return null;
  }

  /**
   * Sincroniza app-input-filter con editRegistroForm. El hijo mantiene answer/answerHidden;
   * sin (close)/(selected) los controles del padre quedan en null → "Faltan campos obligatorios".
   * Mismo patrón que edit-entry-type (closeInputFilter).
   */
  syncInputFilterFromChild(ev: { name: string; value: any }): void {
    if (!ev?.name) return;
    const ctrl = this.editRegistroForm.get(ev.name);
    if (!ctrl) return;
    let v = ev.value;
    if (v === "" || v === "-1" || v === undefined) {
      v = null;
    }
    ctrl.setValue(v);
    ctrl.markAsTouched();
    ctrl.updateValueAndValidity({ emitEvent: true });
  }

  onInputCaracteresProhibidos(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement;
    const valor = input.value ?? "";
    const sanitizado = EditTipoAbonadoComponent.sanitizarCaracteresProhibidos(valor);
    if (sanitizado !== valor) {
      const ctrl = this.editRegistroForm.get(controlName);
      if (ctrl) {
        ctrl.setValue(sanitizado, { emitEvent: true });
        input.value = sanitizado;
      }
    }
  }

  private buildForm(): void {
    const noPermitidos = EditTipoAbonadoComponent.caracteresProhibidosValidator;
    this.editRegistroForm = this.fb.group({
      pkId: [0],
      Codigo: ["", [Validators.maxLength(10), noPermitidos]],
      Nombre: ["", [Validators.required, Validators.maxLength(30), noPermitidos]],
      Descripcion: [
        "",
        [Validators.required, Validators.maxLength(255), noPermitidos],
      ],
      TextoPantalla: ["", [Validators.maxLength(30), noPermitidos]],
      TextoImpresion: ["", [Validators.maxLength(30), noPermitidos]],
      TextoDisplay: ["", [Validators.maxLength(30), noPermitidos]],

      idGrupo: [null, Validators.required], // Grupo tipo abonado
      CategoriaId: [null, Validators.required],
      TipoProductoId: [null],
      TEDatosId: [null],
      CodigoTipoControl: [null],
      GrupoEdadId: [null],
      GrupoReportId: [null],
      CanalId: [
        null,
        this.obligatorioCanalesEntradas ? Validators.required : null,
      ],
      idTipoOperacion: [null],
      TeclaRapida: [null],

      ALaVenta: [null, EditTipoAbonadoComponent.toggleOpcionVentaRequerida],
      TicketVendido: [
        null,
        EditTipoAbonadoComponent.toggleOpcionVentaRequerida,
      ],
      ImprimirTicket: [
        null,
        EditTipoAbonadoComponent.toggleOpcionVentaRequerida,
      ],
      ImpresionConjunta: [false],

      // Taxonomía (obligatorios si taxonomiaActiva)
      TXCentroCosteId: [
        null,
        this.taxonomiaActiva ? Validators.required : null,
      ],
      iTXTipoPrecioId: [
        null,
        this.taxonomiaActiva ? Validators.required : null,
      ],
      iTXCanalVentasId: [
        null,
        this.taxonomiaActiva ? Validators.required : null,
      ],
      iTXItemVentasId: [null],
      Nivel1: [
        null,
        this.taxonomiaActiva ? Validators.required : null,
      ],
      Nivel2: [null],
      Nivel3: [null],
      Nivel4: [null],
      Nivel5: [null],
      Nivel6: [null],
      Nivel7: [null],
    });
    this.aplicarCodigoSoloLectura();
  }

  /** Código generado por API: control deshabilitado, sin required (getRawValue sigue incluyendo el valor). */
  private aplicarCodigoSoloLectura(): void {
    const c = this.editRegistroForm.get("Codigo");
    if (!c) {
      return;
    }
    c.clearValidators();
    const noPermitidos = EditTipoAbonadoComponent.caracteresProhibidosValidator;
    c.setValidators([Validators.maxLength(10), noPermitidos]);
    c.updateValueAndValidity({ emitEvent: false });
    c.disable({ emitEvent: false });
  }

  private async cargarDatosAsync(): Promise<void> {
    this.loading = true;
    try {
      await Promise.all([
        this.cargarGruposTipoAbonado(),
        this.cargarCategorias(),
        this.cargarTiposProductos(),
        this.cargarTiposControl(),
        this.cargarGruposEdad(),
        this.cargarGruposReport(),
        this.cargarTiposEntradaDatos(),
        this.cargarCanales(),
        this.cargarProductos(),
        this.cargarCentros(),
        this.cargarBotonera(),
        this.cargarTaxonomiaLists(),
      ]);

      if ((this.mode === "U" || this.mode === "D") && this.pkId) {
        await this.cargarRegistro(this.pkId);
        if (this.mode === "D") {
         
          this.editRegistroForm.patchValue({ pkId: 0, Codigo: "" }, { emitEvent: false });
        }
      }
    } catch (error) {
      this.httpErrorService.identificarErrores(error);
    }
    this.loading = false;
    this.initTaxonomiaCabeceraWatch();
    this.cdRef.detectChanges();
  }

  private initTaxonomiaCabeceraWatch(): void {
    if (this.taxonomiaCabeceraWatchInited || !this.taxonomiaActiva) {
      return;
    }
    this.taxonomiaCabeceraWatchInited = true;
    const names = ["TXCentroCosteId", "iTXTipoPrecioId", "iTXCanalVentasId"];
    for (const name of names) {
      this.editRegistroForm.get(name)?.valueChanges.subscribe(() => {
        if (this.loading) {
          return;
        }
        this.reiniciarNivelesTaxonomiaTrasCambioCabecera();
      });
    }
  }

  private reiniciarNivelesTaxonomiaTrasCambioCabecera(): void {
    if (!this.taxonomiaActiva || this.loading) {
      return;
    }
    for (let n = 1; n <= 7; n++) {
      this.editRegistroForm
        .get(`Nivel${n}`)
        ?.setValue(null, { emitEvent: false });
      if (n >= 2) {
        this.txNiveles[n] = [];
      }
      this.actualizarValidadorNivel(n, false);
    }
    this.ultimoNivelTaxonomiaLeaf = null;
    this.taxonomiaRevenueCategory = "";
    this.taxonomiaCuentaVisitante = "";
    this.editRegistroForm.patchValue(
      { iTXItemVentasId: null },
      { emitEvent: false }
    );
    void this.cargarNivelTaxonomia(1);
  }

  private async cargarGruposTipoAbonado(): Promise<void> {
    const data = await this.servicioAPI
      .TraerDatosListasAsync("grupotipoabonado")
      .toPromise();
    const raw = data?.DatosListas ?? [];
    this.gruposTipoAbonado = raw.map((g: any) => ({
      ...g,
      Nombre: g.Nombre ?? g.nombre ?? "",
      pkId: g.pkId ?? g.Id ?? g.id,
    }));
  }

  private async cargarCategorias(): Promise<void> {
    const data = await this.servicioAPI.ObtenerCustomanCategorias().toPromise();
    if (data?.DatosResult?.lista) {
      this.categorias = data.DatosResult.lista;
    }
  }

  private async cargarTiposProductos(): Promise<void> {
    const data = await this.servicioAPI.GetTiposProductos().toPromise();
    this.tiposProductos =
      data?.DatosResult?.ListadoTiposProductos ??
      data?.DatosResult?.Lista ??
      [];
  }

  private async cargarTiposControl(): Promise<void> {
    const data = await this.servicioAPI
      .getTiposControl({ page: 1, pagelements: 9999 }, [])
      .toPromise();
    this.tiposControl =
      data?.DatosResult?.customanTiposControl ?? data?.DatosResult?.Lista ?? [];
  }

  private async cargarGruposEdad(): Promise<void> {
    const data = await this.servicioAPI
      .TraerDatosListasAsync("gruposedad")
      .toPromise();
    const raw = data?.DatosListas ?? [];
    this.gruposEdad = raw.map((g: any) => ({
      ...g,
      Nombre: g.Nombre ?? g.nombre ?? "",
      pkId: g.pkId ?? g.Id ?? g.id,
    }));
  }

  private async cargarGruposReport(): Promise<void> {
    if (!this.grupoReportAbonado) {
      return;
    }
    const data = await this.servicioAPI
      .TraerDatosListasAsync("gruposreportabonado")
      .toPromise();
    const raw = data?.DatosListas ?? [];
    this.gruposReport = raw.map((g: any) => ({
      ...g,
      Nombre: g.Nombre ?? g.nombre ?? "",
      pkId: g.pkId ?? g.Id ?? g.id,
    }));
  }

  private async cargarTiposEntradaDatos(): Promise<void> {
    const data = await this.servicioAPI.GetTiposEntradaDatos().toPromise();
    const raw =
      data?.DatosResult?.ListadoTiposEntradaDatos ??
      data?.DatosResult?.Lista ??
      [];
    this.tiposEntradaDatos = Array.isArray(raw)
      ? raw.map((item: any) => ({
          ...item,
          Nombre: item.Nombre ?? item.nombre ?? "",
          pkId:
            item.pkId ??
            item.PkId ??
            item.TEDatosId ??
            item.pkID,
        }))
      : [];
  }

  private async cargarCanales(): Promise<void> {
    if (!this.asociarCentrosCosteCanales && !this.obligatorioCanalesEntradas) {
      return;
    }
    try {
      const data = await this.servicioAPI.ObtenerCustomanCanales();
      const lista = data?.DatosResult?.Lista ?? [];
      /**
       * US: texto = Canal (app-input-filter usa Nombre; mapeamos desde Canal/SubCanal).
       * Valor del select = pkId. Solo ítems con Seleccionable = 1.
       * No usar IdCanal como valor del formulario (puede diferir del pkId de la lista).
       */
      this.canales = lista
        .filter(
          (c: any) =>
            c.Seleccionable === 1 ||
            c.Seleccionable === "1" ||
            c.Seleccionable === true
        )
        .map((c: any) => {
          const canal = (c.Canal ?? c.Nombre ?? "").toString().trim();
          const sub = (c.SubCanal ?? "").toString().trim();
          const etiqueta =
            canal && sub ? `${canal} — ${sub}` : canal || sub || String(c.pkId ?? c.Id ?? "");
          const pk =
            c.pkId !== undefined && c.pkId !== null && c.pkId !== ""
              ? c.pkId
              : c.PkId !== undefined && c.PkId !== null && c.PkId !== ""
              ? c.PkId
              : c.Id;
          return {
            ...c,
            Nombre: etiqueta,
            pkId: pk,
          };
        });
    } catch {
      this.canales = [];
    }
  }

  /**
   * Catálogo de tipos de entrada para el select "Producto".
   * Temporalmente vía GetTipoEntrada (Maestros/GetDatosListas entradas1); Customan/GetTipoEntrada vía getTiposEntradas cuando el entorno lo tenga estable.
   */
  private async cargarProductos(): Promise<void> {
    const data = await this.servicioAPI.GetTipoEntrada().toPromise();
    const lista = data?.DatosResult?.Lista || [];
    this.productosDisponibles = lista;
    this.productosAsignados = [];
    this.productosSinAsignar = [];
  }

  private async cargarTaxonomiaLists(): Promise<void> {
    if (!this.taxonomiaActiva) {
      return;
    }
    try {
      const [centroCoste, tipoPrecio, canalVentas] = await Promise.all([
        this.servicioAPI
          .TraerDatosListasAsync("txcentrocoste" as any)
          .toPromise(),
        this.servicioAPI
          .TraerDatosListasAsync("txtipoprecio" as any)
          .toPromise(),
        this.servicioAPI
          .TraerDatosListasAsync("txcanalventa" as any, {
            NombreCampo: "activo" as any,
            FiltroCampo: "1",
          } as any)
          .toPromise(),
      ]);
      this.txCentroCoste = (centroCoste?.DatosListas ?? []).map((x: any) => ({
        ...x,
        pkId: x.pkId ?? x.Id,
        No: x.No ?? x.pkId ?? x.Id,
        Nombre: x.Nombre ?? x.chCentroCosteTX ?? "",
      }));
      this.txTipoPrecio = (tipoPrecio?.DatosListas ?? []).map((x: any) => ({
        ...x,
        pkId: x.pkId ?? x.Id,
        Nombre: x.Nombre ?? x.chTipoPrecioTX ?? "",
      }));
      this.txCanalVentas = (canalVentas?.DatosListas ?? []).map((x: any) => ({
        ...x,
        pkId: x.pkId ?? x.Id,
        Nombre: x.Nombre ?? x.chCanalVentasTX ?? "",
      }));
      await this.cargarNivelTaxonomia(1);
    } catch (error) {
      this.httpErrorService.identificarErrores(error);
    }
  }

  async cargarNivelTaxonomia(
    nivel: number,
    nivelPredecesorNombre?: string,
    options?: { refreshView?: boolean }
  ): Promise<void> {
    const refreshView = options?.refreshView !== false;
    try {
      const hdr = this.taxonomiaActiva
        ? this.editRegistroForm.getRawValue()
        : null;
      const taxonomiaCabecera =
        hdr &&
        (hdr.TXCentroCosteId ||
          hdr.iTXTipoPrecioId ||
          hdr.iTXCanalVentasId)
          ? {
              TXCentroCosteId: hdr.TXCentroCosteId,
              TXTipoPrecioId: hdr.iTXTipoPrecioId,
              TXCanalVentasId: hdr.iTXCanalVentasId,
            }
          : undefined;
      const data = await this.servicioAPI
        .GetTXItemVentasSegunNivel(
          nivel,
          nivelPredecesorNombre,
          taxonomiaCabecera
        )
        .toPromise();
      const listado = data?.DatosResult?.Listado ?? data?.DatosResult?.Lista ?? [];
      this.txNiveles[nivel] = listado;
      if (listado?.length && this.taxonomiaActiva) {
        this.actualizarValidadorNivel(nivel, true);
      }
      if (refreshView) {
        this.cdRef.detectChanges();
      }
    } catch (error) {
      this.txNiveles[nivel] = [];
      this.actualizarValidadorNivel(nivel, false);
      this.httpErrorService.identificarErrores(error);
      if (refreshView) {
        this.cdRef.detectChanges();
      }
    }
  }

  onNivelTaxonomiaChange(nivel: number, item: any): void {
    for (let n = nivel + 1; n <= 7; n++) {
      this.editRegistroForm.get(`Nivel${n}`)?.setValue(null);
      this.txNiveles[n] = [];
      this.actualizarValidadorNivel(n, false);
    }
    this.taxonomiaRevenueCategory = "";
    this.taxonomiaCuentaVisitante = "";
    this.ultimoNivelTaxonomiaLeaf = null;
    if (item && (item.TieneSucesor === 0 || item.TieneSucesor === "0")) {
      this.ultimoNivelTaxonomiaLeaf = item;
      this.taxonomiaRevenueCategory = item.RevenueCategory ?? "";
      this.taxonomiaCuentaVisitante = this.textoCuentaVisitanteInformativo(
        item.CuentaVisitante
      );
      const patch: Record<string, unknown> = {
        iTXItemVentasId: item.TXItemVentasId ?? item.pkId,
      };
      const cv = EditTipoAbonadoComponent.idTaxonomiaDesdeNodoIfAny(item, [
        "TXCanalVentasId",
        "iTXCanalVentasId",
        "CanalVentasId",
        "CanalVentaId",
      ]);
      if (cv) {
        patch.iTXCanalVentasId = cv;
      }
      const tp = EditTipoAbonadoComponent.idTaxonomiaDesdeNodoIfAny(item, [
        "TXTipoPrecioId",
        "iTXTipoPrecioId",
      ]);
      if (tp) {
        patch.iTXTipoPrecioId = tp;
      }
      const cc = EditTipoAbonadoComponent.idTaxonomiaDesdeNodoIfAny(item, [
        "TXCentroCosteId",
      ]);
      if (cc) {
        patch.TXCentroCosteId = cc;
      }
      // emitEvent: false — si no, valueChanges de cabecera borraría los niveles recién elegidos.
      this.editRegistroForm.patchValue(patch, { emitEvent: false });
    } else if (item) {
      this.cargarNivelTaxonomia(nivel + 1, item.NivelNombre);
      this.actualizarValidadorNivel(nivel + 1, true);
    }
    this.cdRef.detectChanges();
  }

  /** Añade o quita validador required en Nivel N según exista o no. */
  private actualizarValidadorNivel(nivel: number, required: boolean): void {
    const ctrl = this.editRegistroForm.get(`Nivel${nivel}`);
    if (!ctrl) return;
    if (required && this.taxonomiaActiva) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity();
  }

  private async cargarBotonera(): Promise<void> {
    try {
      const data = await this.servicioAPI.GetTipoEntradaBotonera().toPromise();
      const lista = data?.DatosResult?.Lista || [];
      this.botoneraOpciones = lista;
    } catch (error) {
      this.httpErrorService.identificarErrores(error);
    }
  }

  /**
   * mat-option usa [value]="opt.TeclaRapida"; debe coincidir en tipo y valor con el guardado en API.
   */
  private teclaRapidaParaSelect(raw: any): any {
    if (raw === null || raw === undefined || raw === "") return null;
    const s = String(raw).trim();
    const n = parseInt(s, 10);
    for (const opt of this.botoneraOpciones || []) {
      const tr = opt?.TeclaRapida;
      if (tr === raw) return tr;
      if (String(tr ?? "").trim() === s) return tr;
      if (!Number.isNaN(n) && Number(tr) === n) return tr;
    }
    return null;
  }

  /**
   * Igual que otras pantallas (p. ej. edit-temporada-tarifas): lista global de centros vía GetCentros
   * y `centrosSinAsignar` = lista completa de activos (el dual-list filtra duplicados en la izquierda).
   */
  private async cargarCentros(): Promise<void> {
    if (!this.asociarCentros) {
      return;
    }
    await this.gsCentros.getCentros();
    this.centrosAsignados = [];
    this.centrosSinAsignar = (this.gsCentros.tipoCentros || []).filter(
      (c: any) => c.Activo === "True" || c.Activo === true
    );
  }

  /**
   * API suele devolver Taxonomia como objeto anidado (TXCentroCosteId, TXTipoPrecioId, TXNivel1…7, etc.).
   * El formulario usa iTXTipoPrecioId / iTXCanalVentasId y Nivel1–7 con objetos de GetTXItemVentasSegunNivel.
   */
  private taxonomiaPlanaDesdeRegistro(reg: any): {
    TXCentroCosteId: any;
    iTXTipoPrecioId: any;
    iTXCanalVentasId: any;
    iTXItemVentasId: any;
  } {
    const t = reg?.Taxonomia;
    if (t && typeof t === "object") {
      return {
        TXCentroCosteId: t.TXCentroCosteId ?? reg.TXCentroCosteId ?? null,
        iTXTipoPrecioId:
          t.iTXTipoPrecioId ??
          t.TXTipoPrecioId ??
          reg.iTXTipoPrecioId ??
          reg.TXTipoPrecioId ??
          null,
        iTXCanalVentasId:
          t.iTXCanalVentasId ??
          t.TXCanalVentasId ??
          reg.iTXCanalVentasId ??
          reg.TXCanalVentasId ??
          null,
        iTXItemVentasId:
          t.TXItemVentasId ?? t.iTXItemVentasId ?? reg.iTXItemVentasId ?? null,
      };
    }
    return {
      TXCentroCosteId: reg.TXCentroCosteId ?? null,
      iTXTipoPrecioId: reg.iTXTipoPrecioId ?? reg.TXTipoPrecioId ?? null,
      iTXCanalVentasId:
        reg.iTXCanalVentasId ?? reg.TXCanalVentasId ?? null,
      iTXItemVentasId: reg.iTXItemVentasId ?? reg.TXItemVentasId ?? null,
    };
  }

  /** Tras cargar listas TX, reconstruye Nivel1…7 con los objetos correctos (no solo strings TXNivelN). */
  private async restaurarTaxonomiaDesdeRegistro(reg: any): Promise<void> {
    if (!this.taxonomiaActiva) return;
    const tax = reg?.Taxonomia;
    if (!tax || typeof tax !== "object") return;

    for (let n = 2; n <= 7; n++) {
      this.editRegistroForm.get(`Nivel${n}`)?.setValue(null, { emitEvent: false });
      this.txNiveles[n] = [];
      this.actualizarValidadorNivel(n, false);
    }
    this.editRegistroForm.get("Nivel1")?.setValue(null, { emitEvent: false });
    this.taxonomiaRevenueCategory = "";
    this.taxonomiaCuentaVisitante = "";
    this.ultimoNivelTaxonomiaLeaf = null;

    let predecesorNombre: string | undefined;
    let lastItem: any = null;

    for (let n = 1; n <= 7; n++) {
      const rawNv = tax[`TXNivel${n}`];
      if (rawNv === undefined || rawNv === null || String(rawNv).trim() === "") {
        break;
      }
      const target = String(rawNv).trim();
      const nivel1YaCargado =
        n === 1 &&
        predecesorNombre === undefined &&
        Array.isArray(this.txNiveles[1]) &&
        this.txNiveles[1].length > 0;
      if (!nivel1YaCargado) {
        await this.cargarNivelTaxonomia(n, predecesorNombre, {
          refreshView: false,
        });
      } else if (this.taxonomiaActiva) {
        this.actualizarValidadorNivel(1, true);
      }
      const list = this.txNiveles[n] || [];
      const item = list.find(
        (it: any) =>
          String(it?.NivelNombre ?? "").trim() === target ||
          String(it?.TXItemVentasId ?? "") === target ||
          String(it?.pkId ?? "") === target
      );
      if (!item) {
        break;
      }
      lastItem = item;
      this.editRegistroForm.get(`Nivel${n}`)?.setValue(item, { emitEvent: false });
      predecesorNombre = item.NivelNombre;

      const esHoja =
        item.TieneSucesor === 0 ||
        item.TieneSucesor === "0" ||
        n === 7;
      if (esHoja) {
        this.ultimoNivelTaxonomiaLeaf = item;
        break;
      }
    }
    if (!this.ultimoNivelTaxonomiaLeaf && lastItem) {
      const hoja =
        lastItem.TieneSucesor === 0 || lastItem.TieneSucesor === "0";
      if (hoja) {
        this.ultimoNivelTaxonomiaLeaf = lastItem;
      }
    }

    const txItem =
      tax.TXItemVentasId ??
      tax.iTXItemVentasId ??
      lastItem?.TXItemVentasId ??
      lastItem?.pkId;
    if (txItem !== undefined && txItem !== null && txItem !== "") {
      this.editRegistroForm.patchValue(
        { iTXItemVentasId: txItem },
        { emitEvent: false }
      );
    }

    this.taxonomiaRevenueCategory =
      (tax.RevenueCategory ?? lastItem?.RevenueCategory ?? "").toString();
    const cvRaw = tax.CuentaVisitante;
    const cv =
      cvRaw !== undefined && cvRaw !== null && `${cvRaw}` !== ""
        ? cvRaw
        : lastItem?.CuentaVisitante;
    const hoja =
      this.ultimoNivelTaxonomiaLeaf &&
      (this.ultimoNivelTaxonomiaLeaf.TieneSucesor === 0 ||
        this.ultimoNivelTaxonomiaLeaf.TieneSucesor === "0");
    this.taxonomiaCuentaVisitante = hoja
      ? this.textoCuentaVisitanteInformativo(cv)
      : "";
    this.cdRef.detectChanges();
  }

  async cargarRegistro(pkId: string): Promise<void> {
    this.entradasRows = [];
    try {
      const data = await this.servicioAPI.getTiposAbonado(pkId).toPromise();
      const lista = Array.isArray(data?.DatosResult?.Lista)
        ? data.DatosResult.Lista
        : Array.isArray(data?.DatosResult?.ListaTipoAbonado)
        ? data.DatosResult.ListaTipoAbonado
        : [];
      const idStr = String(pkId);
      const reg =
        lista.find(
          (x) => String(x.pkId ?? x.PkId ?? "") === idStr
        ) || lista[0];
      if (reg) {
        const sanit = EditTipoAbonadoComponent.sanitizarCaracteresProhibidos;
        const txFlat = this.taxonomiaPlanaDesdeRegistro(reg);
        this.editRegistroForm.patchValue({
          pkId: reg.pkId || reg.PkId || 0,
          Codigo: sanit(reg.Codigo || ""),
          Nombre: sanit(reg.Nombre || ""),
          Descripcion: sanit(reg.Descripcion || ""),
          TextoPantalla: sanit(reg.TextoPantalla || ""),
          TextoImpresion: sanit(reg.TextoImpresion || ""),
          TextoDisplay: sanit(reg.TextoDisplay || ""),
          idGrupo: reg.GrupoId || reg.idGrupo || null,
          CategoriaId: reg.CategoriaId || null,
          TipoProductoId: reg.TipoProductoId || null,
          TEDatosId: reg.TEDatosId || null,
          CodigoTipoControl: reg.CodigoTipoControl || null,
          GrupoEdadId: reg.GrupoEdadId || null,
          GrupoReportId: reg.GrupoReportId || null,
          CanalId: reg.CanalId || null,
          idTipoOperacion: EditTipoAbonadoComponent.idTipoOperacionParaSelect(
            reg.idTipoOperacion ?? reg.IdTipoOperacion
          ),
          TeclaRapida: this.teclaRapidaParaSelect(
            reg.TeclaRapida ?? reg.teclaRapida
          ),
          ALaVenta: EditTipoAbonadoComponent.flagBooleanoDesdeApi(reg.ALaVenta),
          TicketVendido: EditTipoAbonadoComponent.flagBooleanoDesdeApi(
            reg.TicketVendido
          ),
          ImprimirTicket: EditTipoAbonadoComponent.flagBooleanoDesdeApi(
            reg.ImprimirTicket
          ),
          ImpresionConjunta: EditTipoAbonadoComponent.flagBooleanoDesdeApi(
            reg.ImpresionConjunta
          ),
          TXCentroCosteId: txFlat.TXCentroCosteId,
          iTXTipoPrecioId: txFlat.iTXTipoPrecioId,
          iTXCanalVentasId: txFlat.iTXCanalVentasId,
          iTXItemVentasId: txFlat.iTXItemVentasId,
        }, { emitEvent: false });

        if (Array.isArray(reg.ListaTiposEntradas)) {
          this.entradasRows = reg.ListaTiposEntradas.map((x: any) => {
            const tidRaw = x.TipoEntradaId ?? x.pkId ?? x.EntradaId;
            const tid =
              tidRaw === null || tidRaw === undefined || tidRaw === ""
                ? null
                : String(tidRaw);
            const ctrl =
              x.ControlAcceso !== undefined && x.ControlAcceso !== null
                ? EditTipoAbonadoComponent.controlAccesoDesdeListaApi(
                    x.ControlAcceso
                  )
                : false;
            return {
              _uid: EditTipoAbonadoComponent.newEntradaFilaUid(),
              tipoEntradaId: tid,
              controlAcceso: ctrl,
            };
          });
          // LEGACY dual-list producto (referencia):
          // this.productosAsignados = reg.ListaTiposEntradas.map(...)
          // this.productosSinAsignar = [...this.productosDisponibles];
        } else {
          this.entradasRows = [];
        }

        if (this.asociarCentros) {
          const centrosIds = reg.CentrosIds || reg.centrosIds || "";
          this.centrosAsignados =
            this.gsCentros.daTiposCentros({
              CentrosIds: centrosIds,
              centrosIds: centrosIds,
              CentrosNombres: reg.CentrosNombres,
              centrosNombres: reg.centrosNombres,
            }) || [];
          this.centrosSinAsignar = (this.gsCentros.tipoCentros || []).filter(
            (c: any) => c.Activo === "True" || c.Activo === true
          );
        }

        if (this.taxonomiaActiva && reg.Taxonomia) {
          await this.restaurarTaxonomiaDesdeRegistro(reg);
        }
      }
    } catch (error) {
      this.httpErrorService.identificarErrores(error);
    }
    this.cdRef.detectChanges();
  }

  /**
   * Mensagem genérica ao guardar (igual feature/21537 / edit-entry-type): sem detalhar
   * entradas mínimas, filas, duplicado nem regra de acceso — só aviso de campos obrigatórios.
   */
  private mensajeValidacionCamposRequeridos(): string {
    return (
      this.translation?.FaltanCamposRequeridos ||
      this.translation?.FaltanCamposObligatorios ||
      "Faltan Campos Obligatorios"
    );
  }

  submit(): void {
    this.submitValidationTick++;
    this.submitAttempted = true;
    this.editRegistroForm.markAllAsTouched();

    const filasConProducto = (this.entradasRows || []).filter(
      (r) =>
        r.tipoEntradaId !== null &&
        r.tipoEntradaId !== undefined &&
        String(r.tipoEntradaId).trim() !== ""
    );
    if (!filasConProducto.length) {
      this.snackvar_service.openSnackBar(
        this.mensajeValidacionCamposRequeridos(),
        "",
        "red-snackbar"
      );
      return;
    }
    const filaVacia = (this.entradasRows || []).some(
      (r) =>
        !r.tipoEntradaId ||
        String(r.tipoEntradaId).trim() === ""
    );
    if (filaVacia) {
      this.snackvar_service.openSnackBar(
        this.mensajeValidacionCamposRequeridos(),
        "",
        "red-snackbar"
      );
      return;
    }
    const ids = filasConProducto.map((r) => String(r.tipoEntradaId).trim());
    const uniq = new Set(ids);
    if (uniq.size !== ids.length) {
      this.snackvar_service.openSnackBar(
        this.mensajeValidacionCamposRequeridos(),
        "",
        "red-snackbar"
      );
      return;
    }
    const algunoAccesoOff = filasConProducto.some((r) => !r.controlAcceso);
    if (!algunoAccesoOff) {
      this.snackvar_service.openSnackBar(
        this.mensajeValidacionCamposRequeridos(),
        "",
        "red-snackbar"
      );
      return;
    }

    if (this.editRegistroForm.invalid) {
      this.snackvar_service.openSnackBar(
        this.mensajeValidacionCamposRequeridos(),
        "",
        "red-snackbar"
      );
      return;
    }

    const peticion = this.mapFormToPeticion();

    this.loading = true;
    this.servicioAPI.InsUpdTipoAbonado(peticion).subscribe(
      (response) => {
        this.loading = false;
        if (response?.DatosResult == null && response?.Mensajes?.length > 0) {
          response.Mensajes.forEach((m: any) => {
            const cod = String(m?.CodigoMensaje ?? m?.codigoMensaje ?? "").trim();
            const msg =
              (cod && this.translation?.["CodErr_" + cod]) ||
              m?.DescripcionMensaje ||
              m?.descripcionMensaje ||
              this.translation?.ErrorGenerico ||
              "Error";
            this.snackvar_service.openSnackBar(msg, "", "red-snackbar");
          });
          return;
        }
        this.snackvar_service.openSnackBar(
          this.translation?.GuardadoCorrectamente || "Guardado correctamente",
          "",
          "green-snackbar"
        );
        this.panelService.setDiscardChanges(true);
        this.panelService.setClosePanel(null);
        this.panelService.setSavePanel(null);
      },
      (error) => {
        this.loading = false;
        this.httpErrorService.identificarErrores(error);
      }
    );
  }

  destroy(): void {
    this.panelService.setDiscardChanges(true);
    this.panelService.setClosePanel(null);
    this.panelService.setSavePanel(null);
  }

  discard(): void {
    this.submitAttempted = false;
    this.submitValidationTick = 0;
    this.editRegistroForm.reset();
    this.aplicarCodigoSoloLectura();
    this.entradasRows = [];
    this.productosAsignados = [];
    this.productosSinAsignar = [];
    // LEGACY: this.productosSinAsignar = [...this.productosDisponibles];
    if (this.asociarCentros) {
      this.centrosAsignados = [];
      this.centrosSinAsignar = (this.gsCentros.tipoCentros || []).filter(
        (c: any) => c.Activo === "True" || c.Activo === true
      );
    }
  }

  validateFields(field: string): boolean {
    const ctrl = this.editRegistroForm.get(field);
    return ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched);
  }

  /** Toggles ALaVenta / TicketVendido / ImprimirTicket (null até o utilizador escolher). */
  validateOpcionVentaToggle(
    field: "ALaVenta" | "TicketVendido" | "ImprimirTicket"
  ): boolean {
    const ctrl = this.editRegistroForm.get(field);
    if (!ctrl) {
      return false;
    }
    return !!(
      ctrl.invalid &&
      (ctrl.dirty || ctrl.touched || this.submitAttempted)
    );
  }

  /** Padrão Customman (igual `app-input-filter`). */
  get textoCampoRequerido(): string {
    const t = this.translation;
    return (
      (t && (t["CampoRequerido"] || t["CampoObligatorio"])) || "Campo requerido"
    );
  }

  getErrorMessage(field: string): string {
    const ctrl = this.editRegistroForm.get(field);
    if (ctrl?.errors?.required) {
      return `* ${this.textoCampoRequerido}`;
    }
    if (ctrl?.errors?.caracteresProhibidos) {
      const seq = ctrl.errors.caracteresProhibidos?.secuencia ?? "";
      return (
        this.translation?.CaracteresProhibidos ||
        "No se permiten los caracteres: -- ; /* */ @@. Carácter bloqueado: " + seq
      );
    }
    return "";
  }

  /**
   * Convierte la selección del combo (pkId/Id u objeto fila) al id de negocio que espera InsUpdTipoAbonado
   * (p. ej. TXCanalVentasId en la fila puede diferir del pkId mostrado en input-filter).
   */
  private idTaxonomiaParaApi(
    list: any[],
    idSeleccion: any,
    clavesNegocio: string[]
  ): string {
    let fila: any = null;
    if (
      idSeleccion !== null &&
      idSeleccion !== undefined &&
      idSeleccion !== "" &&
      typeof idSeleccion === "object"
    ) {
      fila = idSeleccion;
    } else if (
      idSeleccion !== null &&
      idSeleccion !== undefined &&
      idSeleccion !== ""
    ) {
      const id = String(idSeleccion);
      fila = (list || []).find((x) => {
        if (!x) return false;
        if (String(x.pkId ?? "") === id || String(x.Id ?? "") === id) {
          return true;
        }
        return clavesNegocio.some((k) => String(x[k] ?? "") === id);
      });
    }
    if (!fila) {
      return EditTipoAbonadoComponent.strId(idSeleccion);
    }
    for (const k of clavesNegocio) {
      const val = fila[k];
      if (val !== null && val !== undefined && `${val}` !== "") {
        return String(val);
      }
    }
    return EditTipoAbonadoComponent.strId(
      fila.pkId ?? fila.Id ?? idSeleccion
    );
  }

  private mapFormToPeticion(): any {
    // getRawValue: incluye controles deshabilitados (value los omite y podría mandar flags a 0).
    const v = this.editRegistroForm.getRawValue();

    const listaTiposEntradas = (this.entradasRows || [])
      .filter(
        (r) =>
          r.tipoEntradaId !== null &&
          r.tipoEntradaId !== undefined &&
          String(r.tipoEntradaId).trim() !== ""
      )
      .map((r) => ({
        TipoEntradaId: EditTipoAbonadoComponent.strId(r.tipoEntradaId),
        ControlAcceso: EditTipoAbonadoComponent.flagString01(r.controlAcceso),
      }));

    const centrosIds = this.asociarCentros
      ? this.gsCentros.obtenercentrosAsociadosconOrden(this.centrosAsignados)
      : "";

    const leaf = this.ultimoNivelTaxonomiaLeaf;
    const itemVentasSel =
      v.iTXItemVentasId ?? leaf ?? null;
    const centroDesdeHoja = EditTipoAbonadoComponent.idTaxonomiaDesdeNodoIfAny(
      leaf,
      ["TXCentroCosteId"]
    );
    const tipoDesdeHoja = EditTipoAbonadoComponent.idTaxonomiaDesdeNodoIfAny(
      leaf,
      ["TXTipoPrecioId", "iTXTipoPrecioId"]
    );
    const canalDesdeHoja = EditTipoAbonadoComponent.idTaxonomiaDesdeNodoIfAny(
      leaf,
      [
        "TXCanalVentasId",
        "iTXCanalVentasId",
        "CanalVentasId",
        "CanalVentaId",
      ]
    );
    const TXCentroCosteId = this.idTaxonomiaParaApi(
      this.txCentroCoste,
      centroDesdeHoja ?? v.TXCentroCosteId,
      ["TXCentroCosteId"]
    );
    const TXTipoPrecioId = this.idTaxonomiaParaApi(
      this.txTipoPrecio,
      tipoDesdeHoja ?? v.iTXTipoPrecioId,
      ["TXTipoPrecioId"]
    );
    const TXCanalVentasId = this.idTaxonomiaParaApi(
      this.txCanalVentas,
      canalDesdeHoja ?? v.iTXCanalVentasId,
      ["TXCanalVentasId"]
    );
    const TXItemVentasId = this.idTaxonomiaParaApi(
      [],
      itemVentasSel,
      ["TXItemVentasId"]
    );
    // InsUpdTipoAbonado: Taxonomía solo los cuatro TX* (sin iTX* en el JSON).
    const taxonomia =
      this.taxonomiaActiva &&
      (TXCentroCosteId ||
        TXTipoPrecioId ||
        TXCanalVentasId ||
        TXItemVentasId)
        ? {
            TXCentroCosteId,
            TXCanalVentasId,
            TXTipoPrecioId,
            TXItemVentasId,
          }
        : null;

    const pkNum =
      v.pkId !== null && v.pkId !== undefined ? Number(v.pkId) : 0;
    const esInsercion = pkNum === 0;
    return {
      pkId: EditTipoAbonadoComponent.strId(
        v.pkId !== null && v.pkId !== undefined ? v.pkId : 0
      ),
      Codigo: esInsercion
        ? ""
        : EditTipoAbonadoComponent.sanitizarCaracteresProhibidos(
            (v.Codigo || "").trim()
          ),
      Nombre: EditTipoAbonadoComponent.sanitizarCaracteresProhibidos(
        (v.Nombre || "").trim()
      ),
      Descripcion: EditTipoAbonadoComponent.sanitizarCaracteresProhibidos(
        v.Descripcion ?? ""
      ),
      ALaVenta: EditTipoAbonadoComponent.flagTrueFalseApi(v.ALaVenta),
      GrupoId: EditTipoAbonadoComponent.strId(v.idGrupo),
      CategoriaId: EditTipoAbonadoComponent.strId(v.CategoriaId),
      TeclaRapida: EditTipoAbonadoComponent.strId(v.TeclaRapida),
      ImprimirTicket: EditTipoAbonadoComponent.flagTrueFalseApi(v.ImprimirTicket),
      TicketVendido: EditTipoAbonadoComponent.flagTrueFalseApi(v.TicketVendido),
      CodigoTipoControl: EditTipoAbonadoComponent.strId(v.CodigoTipoControl),
      idTipoOperacion: EditTipoAbonadoComponent.strId(v.idTipoOperacion),
      TipoProductoId: EditTipoAbonadoComponent.strId(v.TipoProductoId),
      GrupoEdadId: EditTipoAbonadoComponent.strId(v.GrupoEdadId),
      GrupoReportId: EditTipoAbonadoComponent.strId(v.GrupoReportId),
      ListaTiposEntradas: listaTiposEntradas,
      CanalId: EditTipoAbonadoComponent.strId(v.CanalId),
      Taxonomia: taxonomia,
      CentrosIds: centrosIds,
      ImpresionConjunta: EditTipoAbonadoComponent.flagString01(
        v.ImpresionConjunta
      ),
      TextoPantalla: EditTipoAbonadoComponent.sanitizarCaracteresProhibidos(
        v.TextoPantalla ?? ""
      ),
      TextoImpresion: EditTipoAbonadoComponent.sanitizarCaracteresProhibidos(
        v.TextoImpresion ?? ""
      ),
      TextoDisplay: EditTipoAbonadoComponent.sanitizarCaracteresProhibidos(
        v.TextoDisplay ?? ""
      ),
      TEDatosId: EditTipoAbonadoComponent.strId(v.TEDatosId),
      TipoVenta: "4",
    };
  }
}
